/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SucataMaterialDialog from './SucataMaterialDialog';
import { sumSucataItensPeso } from '../../utils/usoMutuoSucataSchema';

const formatPeso = (pesoKg) =>
  Number(pesoKg).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SucataMateriaisSection = ({
  itens,
  onChange,
  disabled,
  error,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const totalPeso = useMemo(() => sumSucataItensPeso(itens), [itens]);

  const handleOpenAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleRemove = (index) => {
    onChange(itens.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSaveMaterial = (material) => {
    if (editingIndex != null) {
      const next = [...itens];
      next[editingIndex] = material;
      onChange(next);
      return;
    }
    onChange([...(itens || []), material]);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>Materiais</Typography>
        {!disabled && (
          <Button variant="contained" size="small" onClick={handleOpenAdd}>
            + Adicionar
          </Button>
        )}
      </Stack>

      {(!itens || itens.length === 0) ? (
        <Typography variant="caption" color="text.secondary">
          Nenhum material adicionado
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {itens.map((item, index) => (
            <Stack
              key={item.id || `${item.tipo}-${index}`}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                py: 0.75,
                px: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="body2">{item.tipoNome}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatPeso(item.pesoKg)} kg
                </Typography>
              </Box>
              {!disabled && (
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => handleOpenEdit(index)} aria-label="Editar material">
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleRemove(index)} aria-label="Remover material">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          ))}
          <Typography variant="body2" fontWeight={600} sx={{ pt: 0.5 }}>
            Total: {formatPeso(totalPeso)} kg
          </Typography>
        </Stack>
      )}

      {error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
          *{error}
        </Typography>
      )}

      <SucataMaterialDialog
        open={dialogOpen}
        item={editingIndex != null ? itens[editingIndex] : null}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveMaterial}
      />
    </Box>
  );
};

SucataMateriaisSection.propTypes = {
  itens: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
};

SucataMateriaisSection.defaultProps = {
  itens: [],
  disabled: false,
  error: null,
};

export default SucataMateriaisSection;
